import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";

import { formatUserNameForAPI } from "../Utility/Utility";
import * as Services from "../Service/UserService";
import { User } from "../Context/UserContext";
import { getDevImpersonationLoginName } from "../Utility/devImpersonation";

/**
 * UserStore - Manages current user state and authentication
 * Fetches user information from /api/me endpoint
 */
export class UserStore {
  rootStore: RootStore;

  // Observable state
  user: User | null = null;
  isLoading: boolean = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  /**
   * Get formatted username for API calls
   * @returns Windows NT formatted username (e.g., "POSITIVE\username")
   */
  get formattedUserName(): string {
    return formatUserNameForAPI(this.user?.LoginName);
  }

  /**
   * Check if user is authenticated
   */
  get isAuthenticated(): boolean {
    return !!this.user?.LoginName;
  }

  /**
   * Fetch current user information from /api/me endpoint
   */
  fetchCurrentUser = async () => {
    this.isLoading = true;
    this.error = null;

    try {
      const impersonateLoginName = getDevImpersonationLoginName();
      const response = impersonateLoginName
        ? await Services.getUserByLoginName(impersonateLoginName)
        : await Services.getCurrentUser();

      // Handle OData response format: { "@odata.context": "...", "value": [userObject] }
      let userData: User | null = null;

      if (response.data) {
        const data = response.data as any;

        // Check if it's an OData response with value array
        if (data.value && Array.isArray(data.value) && data.value.length > 0) {
          userData = data.value[0] as User;
        }
        // Check if response.data is directly the user object
        else if (data.Id !== undefined) {
          userData = data as User;
        }
        // Check if response.data.value is directly the user object (single object, not array)
        else if (data.value && data.value.Id !== undefined) {
          userData = data.value as User;
        }
      }

      runInAction(() => {
        this.user = userData;
        this.isLoading = false;
        this.error = null;
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load user information.";

      // Check if it's an authentication error that's being taken care of by the interceptor
      if (
        errorMessage.includes("interaction_required") ||
        errorMessage.includes("AADSTS160021") ||
        errorMessage.includes("Authentication required") ||
        errorMessage.includes("Authentication in progress")
      ) {
        console.warn(
          "User authentication required or in progress. Waiting for login...",
          error,
        );
        runInAction(() => {
          this.isLoading = false;
          // Don't set error or clear user - let auth interceptor handle redirect
        });
      } else {
        console.error("Failed to fetch current user:", error);
        runInAction(() => {
          this.user = null;
          this.isLoading = false;
          this.error = errorMessage;
        });
      }
    }
  };

  /**
   * Clear user state (for logout)
   */
  clearUser = () => {
    runInAction(() => {
      this.user = null;
      this.error = null;
      this.isLoading = false;
    });
  };

  /**
   * Update user information
   * @param userData - Updated user data
   */
  updateUser = (userData: Partial<User>) => {
    if (!this.user) return;

    runInAction(() => {
      this.user = { ...this.user!, ...userData };
    });
  };
}
